import type { Icon } from "@tabler/icons-react";
import {
	IconChecklist,
	IconCloud,
	IconLayoutGrid,
	IconPlus,
	IconRefresh,
	IconRobot,
	IconUsers,
} from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Alert, Card, Col, Row, Skeleton, Statistic } from "antd";
import { useEffect, useState } from "react";
import {
	useOrganization,
	useOrganizationMetrics,
	useUpdateOrganization,
} from "@/common/artemis-operations/organization";
import { useNotify } from "@/common/use-notify";
import {
	BaseContainer,
	BasePageContainer,
	CustomIcon,
	QueryGuard,
} from "@/components/base";
import { OrganizationDetails } from "@/components/workspaces/organization-details";
import { OrganizationDomains } from "@/components/workspaces/organization-domains";
import { useActions } from "@/contexts/actions-context";
import { usePageTitle } from "@/contexts/page-title-context";

export const Route = createFileRoute("/_authenticated/$orgSlug/admin/")({
	component: AdminSettingsPage,
});

function OrgSection() {
	const notify = useNotify();
	const { data: org, isPending, isError } = useOrganization();
	const updateOrg = useUpdateOrganization();
	const orgItem = Array.isArray(org) ? org[0] : undefined;

	return (
		<QueryGuard
			isPending={isPending}
			isError={isError}
			isNull={false}
			pending={<Skeleton active />}
		>
			<OrganizationDetails
				organization={orgItem}
				onSaveName={(name) =>
					updateOrg.mutate(name, {
						onSuccess: () => notify.success("Organization name updated."),
						onError: (err) =>
							notify.error(
								(err as Error).message || "Failed to update organization name.",
							),
					})
				}
			/>
		</QueryGuard>
	);
}

// One overall number as a card with an icon.
function CounterCard({
	label,
	value,
	icon,
}: {
	label: string;
	value: number;
	icon: Icon;
}) {
	return (
		<Card>
			<Statistic
				title={label}
				value={value.toLocaleString()}
				prefix={<CustomIcon icon={icon} />}
			/>
		</Card>
	);
}

// Org-wide totals; the quotas they count against are on the Billing page.
function UsageSection() {
	const { data: metrics, isPending, isError } = useOrganizationMetrics();

	const counters = metrics && [
		{
			key: "users",
			label: "Users",
			value: metrics.totalUsers,
			icon: IconUsers,
		},
		{
			key: "workspaces",
			label: "Workspaces",
			value: metrics.workspaceCount,
			icon: IconLayoutGrid,
		},
		{
			key: "environments",
			label: "Environments",
			value: metrics.environmentCount,
			icon: IconCloud,
		},
		{
			key: "agents",
			label: "Agents",
			value: metrics.totalAgents,
			icon: IconRobot,
		},
		{
			key: "tasks",
			label: "Tasks",
			value: metrics.taskCount,
			icon: IconChecklist,
		},
	];

	return (
		<BaseContainer title="Current usage">
			<QueryGuard
				isPending={isPending}
				isError={isError}
				isNull={!metrics}
				pending={<Skeleton active paragraph={{ rows: 2 }} />}
			>
				<Row gutter={[16, 16]}>
					{counters?.map((c) => (
						<Col key={c.key} flex="1 1 160px">
							<CounterCard label={c.label} value={c.value} icon={c.icon} />
						</Col>
					))}
				</Row>
			</QueryGuard>
		</BaseContainer>
	);
}

function AdminSettingsPage() {
	const { setTitle } = usePageTitle();
	const { setActions } = useActions();
	const queryClient = useQueryClient();
	useEffect(() => setTitle("Organization settings"), [setTitle]);

	const [addDomainOpen, setAddDomainOpen] = useState(false);

	useEffect(() => {
		setActions([
			{
				type: "button",
				id: "refresh",
				tooltip: "Refresh",
				icon: <CustomIcon icon={IconRefresh} />,
				onClick: () => {
					for (const key of [
						["organization"],
						["organizationMetrics"],
						["orgDomains"],
					]) {
						queryClient.invalidateQueries({ queryKey: key });
					}
				},
			},
			{
				type: "button",
				id: "add-domain",
				tooltip: "Add domain",
				icon: <CustomIcon icon={IconPlus} />,
				onClick: () => setAddDomainOpen(true),
			},
		]);
		return () => setActions([]);
	}, [setActions, queryClient]);

	return (
		<BasePageContainer>
			<BaseContainer title="Organization">
				<OrgSection />
			</BaseContainer>

			<UsageSection />

			<OrganizationDomains
				addOpen={addDomainOpen}
				onCloseAdd={() => setAddDomainOpen(false)}
			/>

			<BaseContainer title="Delete Organization">
				<Alert
					type="info"
					title="This feature is being worked on"
					description={
						<>
							Organization deletion is not yet available in the UI. To delete
							your organization and all associated data, contact us at{" "}
							<a href="mailto:support@stratonext.com">support@stratonext.com</a>
							.
						</>
					}
				/>
			</BaseContainer>
		</BasePageContainer>
	);
}
